import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, TrendingUp, MapPin, DollarSign, Star, FileText, Eye,
  CheckCircle, Clock, Filter, ArrowUpDown, Shield, Award, Lock, Globe,
  ThumbsUp, Send, Building2, Plus, Search
} from "lucide-react";
import { Card, GoldButton } from "./shared/UIComponents";
import { fmtUSD, fmtUSDCompact } from "../utils/currency";
import { formatAddress, estimatedRiskTier, cityStateOnly } from "../utils/addressFormat";
import { MapThumbnail, MetricPill, StatusBadge, ScoreBadge } from "./shared/DesignSystem";
import DealDetailModal from "./DealDetailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const loanTypeLabels = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

export default function MarketplaceDeals({ onOpenTermSheet, onNavigateToLoanBoxes }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("match_score");
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [expressing, setExpressing] = useState(null);
  const [viewedIds, setViewedIds] = useState(new Set());
  const [hasLoanBoxes, setHasLoanBoxes] = useState(true);
  const [browseAll, setBrowseAll] = useState(false);

  const fetchDeals = useCallback(() => {
    setLoading(true);
    const endpoint = browseAll ? `${API}/loan-box/marketplace/deals?all=true` : `${API}/loan-box/marketplace/deals`;
    axios.get(endpoint)
      .then(r => setDeals(r.data.deals || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [browseAll]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // Check if lender has loan boxes
  useEffect(() => {
    axios.get(`${API}/loan-box`)
      .then(r => setHasLoanBoxes((r.data?.boxes || []).length > 0))
      .catch(() => setHasLoanBoxes(false));
  }, []);

  // Fetch viewed deal IDs for the current lender
  useEffect(() => {
    axios.get(`${API}/lender/deals/viewed-ids`)
      .then(r => setViewedIds(new Set(r.data?.viewed_ids || [])))
      .catch(() => {});
  }, []);

  const expressInterest = async (deal) => {
    setExpressing(deal.lead_id);
    try {
      await axios.post(`${API}/portal/lender/deals/${deal.lead_id}/interest`, { message: "Expressed interest via marketplace" });
      fetchDeals();
    } catch (err) { console.error(err); }
    finally { setExpressing(null); }
  };

  const filtered = deals.filter(d => {
    if (filter === "new") return !d.has_expressed_interest && !d.has_term_sheet;
    if (filter === "interested") return d.has_expressed_interest && !d.has_term_sheet;
    if (filter === "quoted") return d.has_term_sheet;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "match_score") {
      // Priority: unviewed deals before viewed deals within the same match score tier
      const aViewed = viewedIds.has(a.lead_id) ? 1 : 0;
      const bViewed = viewedIds.has(b.lead_id) ? 1 : 0;
      // First sort by match_score descending, then by unviewed first
      if (b.match_score !== a.match_score) return b.match_score - a.match_score;
      return aViewed - bViewed;
    }
    if (sortBy === "amount") return (b.requested_loan_amount || 0) - (a.requested_loan_amount || 0);
    if (sortBy === "newest") return new Date(b.created_at) - new Date(a.created_at);
    return 0;
  });

  const scoreColor = (score) => {
    if (score >= 80) return "text-emerald-600 bg-emerald-50 border-emerald-200";
    if (score >= 60) return "text-blue-600 bg-blue-50 border-blue-200";
    return "text-amber-600 bg-amber-50 border-amber-200";
  };

  const riskBadge = (score) => {
    if (!score) return { text: "text-[#999]", bg: "bg-gray-500/20 text-gray-300 border-gray-400/30" };
    if (score >= 75) return { bg: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30" };
    if (score >= 60) return { bg: "bg-amber-500/20 text-amber-300 border-amber-400/30" };
    return { bg: "bg-red-500/20 text-red-300 border-red-400/30" };
  };

  const FALLBACK_PHOTOS = {
    fix_flip: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=600&q=80",
    bridge: "https://images.unsplash.com/photo-1582407947092-a75e4d4aee11?auto=format&fit=crop&w=600&q=80",
    dscr: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80",
    construction: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=80",
    multifamily: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=600&q=80",
    commercial: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80",
    default: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=80",
  };

  const getMapUrl = (address) => {
    if (!address) return null;
    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (key) return `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=14&size=600x300&maptype=roadmap&markers=color:red%7C${encodeURIComponent(address)}&key=${key}`;
    return null;
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  return (
    <div data-testid="marketplace-deals">
      {/* Header Stats */}
      <div className="grid gap-4 sm:grid-cols-4 mb-6">
        {[
          { label: "Matched Deals", value: deals.length, icon: TrendingUp, color: "text-emerald-600" },
          { label: "New Opportunities", value: deals.filter(d => !d.has_expressed_interest && !d.has_term_sheet).length, icon: Star, color: "text-amber-600" },
          { label: "Quoted", value: deals.filter(d => d.has_term_sheet).length, icon: FileText, color: "text-purple-600" },
          { label: "Total Volume", value: `$${(deals.reduce((s, d) => s + (d.requested_loan_amount || 0), 0) / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-gold-dark" },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card hover={false}>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-cream p-2.5 border border-cream-warm"><s.icon className="h-5 w-5 text-emerald-700" /></div>
                <div>
                  <div className="text-2xl font-bold text-[#0A0A0A]">{s.value}</div>
                  <div className="text-xs text-[#888] uppercase tracking-wider">{s.label}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Filter & Sort */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex gap-2">
          {[
            { key: "all", label: "All" },
            { key: "new", label: "New" },
            { key: "interested", label: "Interested" },
            { key: "quoted", label: "Quoted" },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filter === f.key ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-cream-warm text-[#666] hover:border-emerald-200"}`}
              data-testid={`filter-${f.key}`}>
              <Filter className="h-3 w-3 inline mr-1" />{f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {[
            { key: "match_score", label: "Best Match" },
            { key: "amount", label: "Highest Amount" },
            { key: "newest", label: "Newest" },
          ].map(s => (
            <button key={s.key} onClick={() => setSortBy(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${sortBy === s.key ? "bg-blue-100 border-blue-300 text-blue-800" : "bg-white border-cream-warm text-[#666] hover:border-blue-200"}`}
              data-testid={`sort-${s.key}`}>
              <ArrowUpDown className="h-3 w-3 inline mr-1" />{s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Browse-All Banner */}
      {browseAll && (
        <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-2.5 mb-4 flex items-center justify-between" data-testid="mp-browse-all-banner">
          <span className="text-sm text-teal-800">Showing all open-market deals — <button className="underline font-medium" onClick={onNavigateToLoanBoxes}>set up your loan box</button> to see your best matches first.</span>
          <button className="text-xs text-teal-600 underline ml-4" onClick={() => setBrowseAll(false)}>Show matched only</button>
        </div>
      )}

      {/* Deals Grid */}
      {sorted.length === 0 ? (
        <div className="space-y-4">
          {!hasLoanBoxes ? (
            <>
              <div className="rounded-2xl border-2 border-teal-300 bg-teal-50/40 p-8 text-center" data-testid="mp-setup-cta">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-4"><Plus className="h-6 w-6 text-teal-600" /></div>
                <h3 className="text-lg font-bold text-teal-900 mb-2">Set Up Your Lending Criteria to See Matched Deals</h3>
                <p className="text-sm text-teal-700/80 max-w-md mx-auto mb-6">Your loan box defines which deals you see. Takes 2 minutes to configure — we'll automatically match you with deals that fit your criteria.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button onClick={onNavigateToLoanBoxes} className="px-6 py-2.5 rounded-full bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 transition-colors" data-testid="mp-create-loan-box-btn">Create My Loan Box</button>
                  <button onClick={() => setBrowseAll(true)} className="px-6 py-2.5 rounded-full border border-teal-300 text-teal-700 text-sm font-medium hover:bg-teal-50 transition-colors" data-testid="mp-browse-all-btn">Browse All Available Deals</button>
                </div>
              </div>
            </>
          ) : (
            <Card hover={false}>
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-emerald-200 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">No Matching Deals</h3>
                <p className="text-sm text-[#666] mb-4">
                  {deals.length === 0
                    ? "No deals currently match your loan box criteria. New matches will appear here automatically."
                    : "No deals match the current filter. Try adjusting your filters."}
                </p>
                <button onClick={() => setBrowseAll(true)} className="text-sm text-teal-600 underline font-medium" data-testid="mp-browse-all-link">Browse All Available Deals</button>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {sorted.map((deal, i) => {
            const riskScore = deal.summary?.risk_score;
            const loanAmt = deal.requested_loan_amount || deal.purchase_price || 0;
            const ltvPct = deal.ltv ? `${(deal.ltv * 100).toFixed(1)}%` : "N/A";
            const arvDisplay = deal.arv ? fmtUSDCompact(deal.arv) : "N/A";
            const loanDisplay = fmtUSDCompact(loanAmt);

            // Derive address parts
            const formatted = formatAddress(deal.property_address || "");
            const parts = formatted.split(",").map(s => s.trim());
            const street = parts[0] || "Address Pending";
            const cityState = parts.length > 1 ? parts.slice(1).join(", ") : (deal.state || "");

            // AI brief one-liner
            const aiBrief = deal.lender_brief?.headline || deal.lender_brief?.opportunity_summary || "";

            // Stage label
            const displayStatus = deal.has_term_sheet ? "Quoted" : "Open";

            return (
            <motion.div key={deal.lead_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <div className="ic-deal-card" data-testid={`marketplace-deal-${deal.lead_id}`}>
                {/* Map Thumbnail */}
                <div className="ic-deal-card-map" onClick={() => setDetailLeadId(deal.lead_id)}>
                  <MapThumbnail address={deal.property_address} height={140} />
                  <span className="ic-deal-card-map-overlay-lt">
                    {loanTypeLabels[deal.loan_type] || deal.loan_type}
                  </span>
                  {riskScore != null && (
                    <div className="ic-deal-card-map-overlay-score">
                      <ScoreBadge score={riskScore} />
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="ic-deal-card-body">
                  {/* Row 1: Address */}
                  <div className="ic-deal-card-address">{street}</div>
                  <div className="ic-deal-card-city">{cityState}</div>
                  <div className="ic-deal-card-id">{deal.deal_number || `Deal ${deal.lead_id?.substring(0, 8)}`}</div>

                  {/* Row 2: MetricPills */}
                  <div className="ic-deal-card-pills">
                    <MetricPill label="Loan" value={loanDisplay} highlight />
                    <MetricPill label="LTV" value={ltvPct} />
                    <MetricPill label="ARV" value={arvDisplay} />
                  </div>

                  {/* Row 3: Context line */}
                  <div className="ic-deal-card-context">
                    {[
                      deal.experience > 0 ? `${deal.experience} projects` : null,
                      loanTypeLabels[deal.loan_type] || deal.loan_type,
                      deal.close_timeline_days ? `${deal.close_timeline_days} days to close` : null,
                    ].filter(Boolean).join(" \u00B7 ")}
                  </div>

                  {/* Row 4: AI Brief (one line) */}
                  {aiBrief && (
                    <div className="ic-deal-card-ai-brief" title={aiBrief}>
                      {aiBrief}
                    </div>
                  )}

                  {/* Row 5: Status badge */}
                  <div className="ic-deal-card-status">
                    <StatusBadge status={displayStatus} />
                  </div>

                  {/* Row 6: Action Buttons */}
                  <div className="ic-deal-card-buttons">
                    {deal.has_term_sheet ? (
                      <>
                        <button className="ic-btn-outline" style={{ flex: 1, cursor: "default", borderColor: "var(--ic-gold)", color: "var(--ic-gold)" }}>
                          Quoted
                        </button>
                        <button onClick={() => setDetailLeadId(deal.lead_id)} className="ic-btn-outline" data-testid={`mp-view-${deal.lead_id}`}>
                          View
                        </button>
                      </>
                    ) : (
                      <>
                        {deal.has_expressed_interest ? (
                          <button className="ic-btn-outline" style={{ flex: 1, cursor: "default", borderColor: "var(--ic-green-light)", color: "var(--ic-green-light)" }}>
                            Interest
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); expressInterest(deal); }}
                            disabled={expressing === deal.lead_id}
                            className="ic-btn-primary"
                            data-testid={`mp-interest-${deal.lead_id}`}
                          >
                            {expressing === deal.lead_id ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> : null}
                            Interest
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); onOpenTermSheet?.(deal); }}
                          className="ic-btn-outline"
                          data-testid={`mp-submit-ts-${deal.lead_id}`}
                        >
                          Quote
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
            );
          })}
        </div>
      )}

      {/* Deal Detail Modal */}
      {detailLeadId && <DealDetailModal leadId={detailLeadId} onClose={() => setDetailLeadId(null)} viewerRole="lender" />}
    </div>
  );
}
